// The side menu: one group per area, each item with its icon and, where something waits on the
// operator, its counter. It offers only the screens the session's scopes read; until they are
// read, or when they cannot be, it offers every screen and Power decides.
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Result } from 'effect';
import {
  Activity,
  AppWindow,
  BookOpenText,
  ChartColumn,
  Database,
  FileText,
  Gauge,
  GitCompareArrows,
  Inbox,
  KeyRound,
  Landmark,
  Rocket,
  ServerCog,
  ShieldCheck,
  UsersRound,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { sessionScopesQueryOptions } from '../opin-applications/client/edge.ts';
import { scopesGrant } from '../permissions/session-permissions.ts';
import type { SessionPermission } from '../permissions/session-permissions.ts';
import { NavCounter } from './counters/nav-counters.tsx';
import { navGroupsFor } from './navigation.ts';
import type { NavItem } from './navigation.ts';

export const navIconSize = 16;

const NAV_ICON = {
  inicio: ShieldCheck,
  indicadores: ChartColumn,
  onboarding: Workflow,
  conformidade: Landmark,
  'drift-regulatorio': GitCompareArrows,
  'testes-endpoint': Database,
  dcr: KeyRound,
  pcm: Gauge,
  receptora: Inbox,
  'relatorio-diretor': FileText,
  monitoramento: Activity,
  operacoes: ServerCog,
  'primeiros-passos': Rocket,
  aplicacoes: AppWindow,
  documentacao: BookOpenText,
  usuarios: UsersRound,
} as const satisfies Record<NavItem['id'], LucideIcon>;

const NavIcon = ({ id }: { readonly id: NavItem['id'] }): JSX.Element => {
  const Icon = NAV_ICON[id];
  return <Icon aria-hidden="true" size={navIconSize} strokeWidth={2} />;
};

const EVERY_SCREEN = (): boolean => true;

// What the session's scopes grant, once read; every screen before that or when they cannot be.
const useGrants = (): ((permission: SessionPermission) => boolean) => {
  const query = useQuery(sessionScopesQueryOptions);
  if (query.status !== 'success' || Result.isFailure(query.data)) return EVERY_SCREEN;
  const scopes = query.data.success;
  return (permission) => scopesGrant(scopes, permission);
};

export const MainNav = (): JSX.Element => (
  <nav className="admin-nav" aria-label="Navegação principal">
    {navGroupsFor(useGrants()).map(({ group, items }) => (
      <fieldset className="admin-nav-group" key={group.id}>
        <legend className="admin-nav-group-label">{group.label}</legend>
        {items.map((item) => (
          <Link key={item.id} to={item.to}>
            <NavIcon id={item.id} />
            <span>{item.label}</span>
            <NavCounter id={item.id} />
          </Link>
        ))}
      </fieldset>
    ))}
  </nav>
);
